import React from 'react';
import { EmptyResult } from '@components';
import { useAppDispatch, useAppSelector } from '@hooks/reduxHooks.ts';
import { useLingui } from '@lingui/react/macro';
import { getFullUserInfo } from '@routes/___shared___/user.slice.ts';
import { getGroupedNotificationsByDateRange } from '@routes/sidebar/notifications/___shared___/helpers.ts';
import type { NotificationDetails } from '@routes/sidebar/notifications/api/notifications.api.ts';
import {
    getNotifications,
    setScrollTopPosition,
    toggleReadAllNotifications,
} from '@routes/sidebar/notifications/model/notifications.slice.ts';
import {
    CardDaySeparator,
    CardsListWrapper,
    MarkAllAsRead,
} from '@routes/sidebar/notifications/ui/card-list/styled.tsx';
import NotificationCard from '@routes/sidebar/notifications/ui/notification-card';
import { isEmpty } from 'lodash';

interface Props {
    notifications: NotificationDetails[];
    showMarkAllAsRead?: boolean;
}

const CardsList: React.FC<Props> = ({ notifications, showMarkAllAsRead = true }) => {
    const { t } = useLingui();
    const dispatch = useAppDispatch();
    const notificationRequest = useAppSelector((state) => state.notifications.notificationRequest);

    const translatedLabels: Record<string, string> = {
        Today: t`Today`,
        Yesterday: t`Yesterday`,
        'Last Week': t`Last Week`,
        'Last Month': t`Last Month`,
        'Last 3 Months': t`Last 3 Months`,
        Older: t`Older`,
    };

    if (isEmpty(notifications)) {
        return (
            <EmptyResult
                title={t`No notifications`}
                description={t`Provided criteria doesnt match with your notifications`}
            />
        );
    }

    const groupedNotifications = getGroupedNotificationsByDateRange(notifications);

    const handleMarkAllAsRead = () => {
        dispatch(setScrollTopPosition(null));

        dispatch(toggleReadAllNotifications(true))
            .then(() => {
                void dispatch(getFullUserInfo());
                void dispatch(getNotifications(notificationRequest));
            })
            .catch(() => {});
    };

    return (
        <CardsListWrapper>
            {!!showMarkAllAsRead && <MarkAllAsRead onClick={handleMarkAllAsRead}>{t`Mark All as Read`}</MarkAllAsRead>}
            {Object.entries(groupedNotifications).map(([sectionTitle, group], index) =>
                isEmpty(group) ? null : (
                    <React.Fragment key={sectionTitle}>
                        <CardDaySeparator $isFirstItem={index === 0}>{translatedLabels[sectionTitle]}</CardDaySeparator>
                        {group.map((n) => (
                            <NotificationCard note={n} key={n.id} />
                        ))}
                    </React.Fragment>
                ),
            )}
        </CardsListWrapper>
    );
};

export default CardsList;
